import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { CollectionMetafields } from '../../lib/types'
import { useI18n } from '../i18n.client'
import styles from '../../assets/scss/components/collections/related-collections.module.scss'
import { classNames } from '@shopify/css-utilities'
import { Image, Link } from '@shopify/hydrogen'
import { Swiper, SwiperSlide, SwiperClass } from 'swiper/react'
import { FreeMode, Navigation } from 'swiper/modules'
import { BreakpointsContext } from '../layout/breakpoints-provider.client'

export const RelatedCollections: FC<{ collection?: CollectionMetafields }> = ({ collection }) => {
  const { isMediumUp } = useContext(BreakpointsContext)
  const [i18n] = useI18n()
  const [clientWidth, setClientWidth] = useState(0)
  const [swiperRef, setSwiperRef] = useState<SwiperClass | null>(null)
  const navigationPrevRef = useRef<HTMLDivElement>(null)
  const navigationNextRef = useRef<HTMLDivElement>(null)

  const handleResize = useCallback(() => {
    setClientWidth(document.body.clientWidth)
  }, [])

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  const relatedNodes = collection?.relatedCollections?.references?.nodes ?? []

  const handlePrevClick = useCallback(() => {
    swiperRef?.slidePrev()
  }, [swiperRef])

  const handleNextClick = useCallback(() => {
    swiperRef?.slideNext()
  }, [swiperRef])

  if (relatedNodes.length === 0) return null

  return (
    <section className="section">
      <div className={classNames('px', styles.header)}>
        {i18n.translate('got-any-specific-criteria')}
        <div className={styles.navigationButtons}>
          <div className={styles.navigationPrev} onClick={handlePrevClick} ref={navigationPrevRef}>
            <Image alt={i18n.translate('previous')} height={8} src="/carousel-arrow.svg" width={27} />
          </div>
          <div className={styles.navigationNext} onClick={handleNextClick} ref={navigationNextRef}>
            <Image alt={i18n.translate('next')} height={8} src="/carousel-arrow.svg" width={27} />
          </div>
        </div>
      </div>
      <div className={styles.collections} style={{ width: `${clientWidth}px` }}>
        {isMediumUp ? (
          <Swiper
            className={styles.swiper}
            freeMode
            modules={[FreeMode, Navigation]}
            onBeforeInit={(swiper) => {
              swiper.params.navigation.prevEl = navigationPrevRef.current
              swiper.params.navigation.nextEl = navigationNextRef.current
            }}
            onSwiper={setSwiperRef}
            slidesPerGroup={1}
            slidesPerView="auto"
            spaceBetween={40}>
            {relatedNodes.map((relatedCollection) => (
              <RelatedCollectionLink collection={relatedCollection} key={relatedCollection.id} />
            ))}
          </Swiper>
        ) : (
          <>
            {relatedNodes.map((relatedCollection) => (
              <RelatedCollectionLink collection={relatedCollection} key={relatedCollection.id} />
            ))}
          </>
        )}
      </div>
    </section>
  )
}

const RelatedCollectionLink: FC<{ collection: CollectionMetafields }> = ({ collection }) => {
  const ref = useRef<HTMLAnchorElement>(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect()
      setWidth(rect.width)
    }
  }, [])

  return (
    <SwiperSlide style={{ width: `${width}px` }}>
      <Link className={styles.link} ref={ref} to={`/collections/${collection.handle}`}>
        <span className={styles.linkName}>{collection.title}</span>
      </Link>
    </SwiperSlide>
  )
}

RelatedCollectionLink.displayName = 'SwiperSlide'
